/**
 * Errores de Auth0 SPA que indican sesión no renovable en silencio
 * (sin offline_access / refresh tokens: iframe prompt=none u cache inconsistente).
 */
const SESSION_EXPIRED_ERROR_CODES = new Set([
  'login_required',
  'consent_required',
  'interaction_required',
  'missing_refresh_token',
  'invalid_grant',
  'invalid_token',
]);

type Auth0LikeError = {
  error?: string;
  code?: string;
  message?: string;
  error_description?: string;
  cause?: unknown;
};

const readErrorCode = (value: unknown): string | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const err = value as Auth0LikeError & {
    name?: string;
    error?: unknown;
    suppressed?: unknown;
  };

  if (typeof err.error === 'string' && err.error.length > 0) {
    return err.error;
  }
  if (typeof err.code === 'string' && err.code.length > 0) {
    return err.code;
  }

  // SuppressedError (Auth0 puede envolver fallos concurrentes iframe + cache)
  if (err.name === 'SuppressedError') {
    return readErrorCode(err.error) ?? readErrorCode(err.suppressed) ?? readErrorCode(err.cause);
  }

  return readErrorCode(err.cause);
};

const messageSuggestsSessionExpired = (message: string): boolean => {
  const lower = message.toLowerCase();
  return (
    lower.includes('missing_refresh_token') ||
    lower.includes('login_required') ||
    lower.includes('consent_required') ||
    lower.includes('interaction_required') ||
    lower.includes('invalid_grant') ||
    lower.includes('login required')
  );
};

/** True si el fallo de getAccessTokenSilently exige re-login interactivo. */
export const isAuth0SessionExpiredError = (error: unknown): boolean => {
  const code = readErrorCode(error);
  if (code && SESSION_EXPIRED_ERROR_CODES.has(code)) {
    return true;
  }

  if (error instanceof Error && messageSuggestsSessionExpired(error.message)) {
    return true;
  }

  if (
    error &&
    typeof error === 'object' &&
    typeof (error as Auth0LikeError).message === 'string' &&
    messageSuggestsSessionExpired((error as Auth0LikeError).message as string)
  ) {
    return true;
  }

  if (
    error &&
    typeof error === 'object' &&
    typeof (error as Auth0LikeError).error_description === 'string' &&
    messageSuggestsSessionExpired((error as Auth0LikeError).error_description as string)
  ) {
    return true;
  }

  return false;
};

/**
 * Limpia almacenamiento local del navegador relacionado con Auth0.
 * Las cookies del dominio Auth0 solo se limpian vía endpoint de logout.
 */
export const clearAuth0Storage = (): void => {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.includes('auth0') || key.includes('@@auth0spajs') || key.includes('@@auth0spajs::'))
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        console.warn(`Error al eliminar clave de localStorage: ${key}`, e);
      }
    });

    const sessionKeysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (
        key &&
        (key.includes('auth0') || key.includes('@@auth0spajs') || key.includes('@@auth0spajs::'))
      ) {
        sessionKeysToRemove.push(key);
      }
    }
    sessionKeysToRemove.forEach((key) => {
      try {
        sessionStorage.removeItem(key);
      } catch (e) {
        console.warn(`Error al eliminar clave de sessionStorage: ${key}`, e);
      }
    });

    const appCookies = document.cookie.split(';');
    appCookies.forEach((cookie) => {
      const cookieName = cookie.split('=')[0].trim();
      if (cookieName.includes('auth0') || cookieName.includes('auth0spajs')) {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
        const domainParts = window.location.hostname.split('.');
        if (domainParts.length > 1) {
          const parentDomain = '.' + domainParts.slice(-2).join('.');
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${parentDomain}`;
        }
      }
    });
  } catch (error) {
    console.warn('Error al limpiar almacenamiento de Auth0:', error);
  }
};

let reauthInFlight: Promise<void> | null = null;

/** Evita múltiples logout/redirect concurrentes ante fallos de token en paralelo. */
export const runSessionReauthOnce = (reauth: () => Promise<void>): Promise<void> => {
  if (!reauthInFlight) {
    reauthInFlight = reauth().finally(() => {
      reauthInFlight = null;
    });
  }
  return reauthInFlight;
};
